<template>
  <v-container>
    <!-- Loader -->
    <v-row v-if="isLoading" justify="center" class="py-5">
      <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
    </v-row>

    <!-- Group Details -->
    <template v-else-if="group">
      <v-card class="gradient-card pa-4 mb-6">
        <v-row>
          <v-col cols="12" md="6" class="d-flex justify-center">
            <v-img :src="group.imageURL || defaultImage" class="group-image"></v-img>
          </v-col>
          <v-col cols="12" md="6" class="d-flex flex-column justify-center">
            <h2 class="group-title">{{ group.name }}</h2>
            <p class="text-body-2 text-secondary">{{ group.description }}</p>
            <v-chip color="primary" dark class="mr-2">{{ groupMembers.length }} Members</v-chip>
            <v-chip color="info" dark>Created by {{ creatorName }}</v-chip>

            <!-- Join Group Button -->
            <v-btn
              v-if="!isMember && userUID"
              class="mt-4 soft-button"
              color="primary"
              @click="joinGroup"
            >
              Join Group
            </v-btn>

            <!-- Leave Group Button (non-creator members) -->
            <v-btn
              v-if="isMember && !isCreator && userUID"
              class="mt-4 soft-button"
              color="error"
              @click="leaveGroup"
            >
              Leave Group
            </v-btn>

            <!-- Delete Group (Only for creator) -->
            <v-btn v-if="isCreator" class="mt-4" color="error" @click="deleteGroup">
              Delete Group
            </v-btn>
          </v-col>
        </v-row>
      </v-card>

      <!-- Member List -->
      <h3 class="section-title">Members</h3>
      <v-row class="mt-2">
        <v-col
          v-for="member in groupMembers"
          :key="member.id"
          cols="12"
          sm="6"
          md="4"
        >
          <v-card class="hover-card gradient-card pa-2">
            <v-img :src="member.image || defaultUserImage" height="150" class="rounded"></v-img>
            <v-card-title>{{ member.name }}</v-card-title>
            <v-card-actions>
              <v-btn text color="primary" @click="$router.push(`/member-profile/${member.id}`)">
                View Profile
              </v-btn>
              <v-btn
                v-if="isCreator && member.id !== userUID"
                text
                color="error"
                @click="removeMember(member.id)"
              >
                Remove
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Post List -->
      <h3 class="section-title mt-6">Posts</h3>
      <v-row>
        <v-col
          v-for="post in posts"
          :key="post.id"
          cols="12"
          md="6"
        >
          <v-card class="hover-card gradient-card pa-3">
            <div class="d-flex align-center mb-2">
              <v-avatar size="40">
                <v-img :src="post.userProfileImageURL || defaultUserImage"></v-img>
              </v-avatar>
              <div class="ml-3">
                <div class="font-weight-bold">{{ post.userName }}</div>
                <small class="text-grey-500">{{ formatTimestamp(post.timestamp) }}</small>
              </div>
            </div>
            <v-card-text class="pb-0">
              <p style="color: white;">{{ post.text }}</p>
              <v-img v-if="post.imageURL" :src="post.imageURL" class="rounded my-3" height="200" contain></v-img>
              <video v-if="post.videoURL" controls :src="post.videoURL" class="rounded my-3" style="width: 100%; max-height: 300px;"></video>
            </v-card-text>
            <v-divider class="my-2"></v-divider>
            <v-card-actions class="align-center">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    v-bind="attrs"
                    v-on="on"
                    @click="toggleLike(post)"
                  >
                    <v-icon color="red" v-if="post.likes.includes(userUID)">mdi-heart</v-icon>
                    <v-icon v-else>mdi-heart-outline</v-icon>
                  </v-btn>
                </template>
                <span style="white-space: pre-line; max-width: 200px;">
                  <template v-if="post.likedUserNames && post.likedUserNames.length">
                    Liked by:
                    <br />
                    {{ post.likedUserNames.join('\n') }}
                  </template>
                  <template v-else>
                    No likes yet
                  </template>
                </span>
              </v-tooltip>
              <span>{{ post.likes.length }}</span>

              <v-btn icon @click="openComments(post)">
                <v-icon>mdi-comment-outline</v-icon>
              </v-btn>
              <span>{{ post.commentsCount }}</span>

              <!-- Delete button only visible if current user owns the post -->
              <v-btn
                v-if="post.userID === userUID"
                icon
                color="red"
                @click="deletePost(post.id)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Floating Button -->
      <v-btn
        color="primary"
        dark
        fab
        large
        class="floating-button"
        @click="createNewPost"
      >
        <v-icon>mdi-plus</v-icon>
      </v-btn>
    </template>

    <!-- Error -->
    <template v-else>
      <v-alert type="error" class="mt-5">
        Group not found.
      </v-alert>
    </template>

    <!-- ✅ Snackbar -->
    <v-snackbar
      v-model="showSnackbar"
      :timeout="3000"
      color="success"
      elevation="6"
      top
    >
      {{ snackbarText }}
    </v-snackbar>

    <v-bottom-navigation app background-color="#3b5d58" grow dark>
      <v-btn icon to="/home">
        <span>
          <v-icon>mdi-home</v-icon>
          <p class="tab-label">Home</p>
        </span>
      </v-btn>
      <v-btn icon to="/search">
        <span>
          <v-icon>mdi-magnify</v-icon>
          <p class="tab-label">Search</p>
        </span>
      </v-btn>
      <v-btn icon to="/explore">
        <span>
          <v-icon>mdi-compass-outline</v-icon>
          <p class="tab-label">Explore</p>
        </span>
      </v-btn>
      <v-btn icon to="/messages">
        <span>
          <v-icon>mdi-message-text-outline</v-icon>
          <p class="tab-label">Messages</p>
        </span>
      </v-btn>
      <v-btn icon to="/groups">
        <span>
          <v-icon>mdi-account-group</v-icon>
          <p class="tab-label">Groups</p>
        </span>
      </v-btn>
      <v-btn icon @click="goToProfile">
        <span>
          <v-icon>mdi-account</v-icon>
          <p class="tab-label">Profile</p>
        </span>
      </v-btn>
    </v-bottom-navigation>
  </v-container>
</template>

<script>
import { 
  doc, 
  getDoc, 
  updateDoc, 
  collection, 
  query, 
  where, 
  getDocs,
  deleteDoc
} from "firebase/firestore";
import { db, auth } from "@/firebase";

export default {
  props: ["id"],
  data() {
    return {
      group: null,
      groupMembers: [],
      posts: [],
      creatorName: "",
      isCreator: false,
      isMember: false,
      userUID: null,
      isLoading: true,
      defaultImage: "https://via.placeholder.com/200",
      defaultUserImage: "https://via.placeholder.com/100",
      showSnackbar: false,
      snackbarText: "",
    };
  },
  methods: {
    async fetchGroupDetails() {
      try {
        this.isLoading = true;
        const groupRef = doc(db, "groups", this.id);
        const groupSnap = await getDoc(groupRef);

        if (groupSnap.exists()) {
          this.group = { id: groupSnap.id, ...groupSnap.data() };
          this.userUID = auth.currentUser?.uid;

          this.isCreator = this.group.creatorID === this.userUID;
          this.isMember = this.group.members?.includes(this.userUID);

          const creatorRef = doc(db, "user", this.group.creatorID);
          const creatorSnap = await getDoc(creatorRef);
          this.creatorName = creatorSnap.data()?.fullName || "Unknown";

          await this.fetchGroupMembers();
          await this.fetchPosts();
        } else {
          console.error("Group not found.");
        }
      } catch (error) {
        console.error("Error fetching group details:", error);
      } finally {
        this.isLoading = false;
      }
    },
    async fetchGroupMembers() {
      try {
        const members = await Promise.all(
          this.group.members.map(async (memberID) => {
            const memberRef = doc(db, "user", memberID);
            const memberSnap = await getDoc(memberRef);
            return { id: memberSnap.id, ...memberSnap.data() };
          })
        );
        this.groupMembers = members;
      } catch (error) {
        console.error("Error fetching group members:", error);
      }
    },
    async fetchPosts() {
      try {
        const postsRef = collection(db, "posts");
        const q = query(postsRef, where("groupID", "==", this.group.id));
        const querySnapshot = await getDocs(q);

        const rawPosts = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        const postsWithExtras = await Promise.all(
          rawPosts.map(async (post) => {
            // Fetch liker names if any
            let likedUserNames = [];
            if (post.likes && post.likes.length > 0) {
              likedUserNames = await Promise.all(
                post.likes.map(async (uid) => {
                  try {
                    const userRef = doc(db, "user", uid);
                    const userSnap = await getDoc(userRef);
                    return userSnap.exists() ? userSnap.data().fullName || "Unknown" : "Unknown";
                  } catch {
                    return "Unknown";
                  }
                })
              );
            }

            // Use commentsCount from post document or fallback to 0
            const commentsCount = post.commentsCount || 0;

            return {
              ...post,
              likedUserNames,
              commentsCount,
            };
          })
        );

        this.posts = postsWithExtras;
      } catch (error) {
        console.error("Error fetching posts:", error);
      }
    },
    formatTimestamp(timestamp) {
      return new Date(timestamp.seconds * 1000).toLocaleString();
    },
    async toggleLike(post) {
      if (!this.userUID) return; // Make sure user is logged in

      try {
        const postRef = doc(db, "posts", post.id);
        let updatedLikes;

        if (post.likes && post.likes.includes(this.userUID)) {
          // User already liked, so remove the like
          updatedLikes = post.likes.filter(uid => uid !== this.userUID);
        } else {
          // User hasn't liked yet, so add the like
          updatedLikes = post.likes ? [...post.likes, this.userUID] : [this.userUID];
        }

        // Update Firestore
        await updateDoc(postRef, { likes: updatedLikes });

        // Update local posts array
        const postIndex = this.posts.findIndex(p => p.id === post.id);
        if (postIndex !== -1) {
          // Also update likedUserNames for UI immediately
          let likedUserNames = [];

          if (updatedLikes.length > 0) {
            likedUserNames = await Promise.all(
              updatedLikes.map(async (uid) => {
                try {
                  const userRef = doc(db, "user", uid);
                  const userSnap = await getDoc(userRef);
                  return userSnap.exists() ? userSnap.data().fullName || "Unknown" : "Unknown";
                } catch {
                  return "Unknown";
                }
              })
            );
          }

          this.$set(this.posts, postIndex, {
            ...post,
            likes: updatedLikes,
            likedUserNames,
          });
        }
      } catch (error) {
        console.error("Error toggling like:", error);
      }
    },
    viewComments(post) {
      this.$router.push(`/group/${this.group.id}/post/${post.id}`);
    },
    createNewPost() {
      this.$router.push(`/group/${this.group.id}/create-post`);
    },
    async removeMember(memberID) {
      if (!confirm("Are you sure you want to remove this member?")) return;
      try {
        const groupRef = doc(db, "groups", this.group.id);
        const updatedMembers = this.group.members.filter((id) => id !== memberID);
        await updateDoc(groupRef, { members: updatedMembers });
        this.group.members = updatedMembers;
        this.groupMembers = this.groupMembers.filter((member) => member.id !== memberID);
        alert("Member removed successfully.");
      } catch (error) {
        console.error("Error removing member:", error);
      }
    },
    openComments(post) {
      this.$router.push(`/post/${post.id}/comments`);
    },
    async deleteGroup() {
      if (!confirm("Are you sure you want to delete this group?")) return;
      try {
        const groupRef = doc(db, "groups", this.group.id);
        await deleteDoc(groupRef);
        this.$router.push("/groups");
        alert("Group deleted successfully.");
      } catch (error) {
        console.error("Error deleting group:", error);
      }
    },
    async deletePost(postID) {
      if (!confirm("Are you sure you want to delete this post?")) return;
      try {
        const postRef = doc(db, "posts", postID);
        await deleteDoc(postRef);
        this.posts = this.posts.filter(post => post.id !== postID);
        alert("Post deleted successfully.");
      } catch (error) {
        console.error("Error deleting post:", error);
      }
    },

    // Join Group
    async joinGroup() {
      try {
        const groupRef = doc(db, "groups", this.group.id);
        const updatedMembers = [...(this.group.members || []), this.userUID];

        await updateDoc(groupRef, { members: updatedMembers });

        this.group.members = updatedMembers;
        this.isMember = true;

        await this.fetchGroupMembers();

        this.snackbarText = "You've successfully joined the group!";
        this.showSnackbar = true;
      } catch (error) {
        console.error("Error joining group:", error);
        this.snackbarText = "Failed to join group. Please try again.";
        this.showSnackbar = true;
      }
    },

    // Leave Group
    async leaveGroup() {
      if (!confirm("Are you sure you want to leave this group?")) return;
      try {
        const groupRef = doc(db, "groups", this.group.id);
        const updatedMembers = this.group.members.filter(id => id !== this.userUID);

        await updateDoc(groupRef, { members: updatedMembers });

        this.group.members = updatedMembers;
        this.isMember = false;

        await this.fetchGroupMembers();

        this.snackbarText = "You've left the group.";
        this.showSnackbar = true;
      } catch (error) {
        console.error("Error leaving group:", error);
        this.snackbarText = "Failed to leave group. Please try again.";
        this.showSnackbar = true;
      }
    }
  },
  async mounted() {
    await this.fetchGroupDetails();
  },
};
</script>

<style scoped>
.group-image {
  border-radius: 12px;
  max-width: 100%;
  height: auto;
}

.group-title {
  font-size: 2rem;
  font-weight: bold;
  color: white;
}

.text-secondary {
  color: rgba(255, 255, 255, 0.8);
}

.section-title {
  font-size: 1.75rem;
  font-weight: bold;
  color: olive;
  margin-bottom: 16px;
}

.gradient-card {
  background: linear-gradient(135deg, #f2c46d, #3b5d58);
  color: white;
  border-radius: 16px;
  overflow: hidden;
}

.hover-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.hover-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

.floating-button {
  position: fixed;
  bottom: 24px;
  right: 24px;
  background: linear-gradient(135deg, #f2c46d, #3b5d58);
  color: white;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.3);
}
</style>